/**
 * Where the member app is served from, as seen by the admin app.
 *
 * Needed by the page builder's device preview, which embeds a member route in an
 * iframe and drives it over `postMessage` — that requires an explicit target
 * origin (never `'*'`, which would broadcast the draft to whatever happened to
 * be framed).
 *
 * Resolution order, most explicit first:
 *  1. `VITE_MEMBER_URL` — set it in local dev, where admin and member are two
 *     ports on localhost and nothing is derivable.
 *  2. Derived from `VITE_API_URL` by dropping the `api.` label. Production serves
 *     `api.<domain>` and the member app at the apex (see Caddyfile.prod), so this
 *     is correct on every deployed box without extra configuration.
 *  3. Derived from the admin's own host by dropping the `admin.` label — the
 *     backstop if the API is on an unrelated host.
 *
 * Returns null when nothing resolves, which the preview treats as "fall back to
 * the in-page renderer" rather than framing a guessed origin.
 */
export function memberOrigin(): string | null {
  const explicit = import.meta.env.VITE_MEMBER_URL;
  if (explicit) return String(explicit).replace(/\/$/, '');

  const apiUrl = import.meta.env.VITE_API_URL;
  if (apiUrl) {
    try {
      const u = new URL(String(apiUrl));
      if (u.hostname.startsWith('api.')) {
        u.hostname = u.hostname.slice(4);
        return u.origin;
      }
    } catch {
      /* fall through to the host-derived form */
    }
  }

  if (typeof window !== 'undefined') {
    const { protocol, hostname, port } = window.location;
    if (hostname.startsWith('admin.')) {
      return `${protocol}//${hostname.slice(6)}${port ? `:${port}` : ''}`;
    }
  }
  return null;
}

/** The preview route inside the member app. */
export function memberPreviewUrl(): string | null {
  const origin = memberOrigin();
  return origin ? `${origin}/__rp-preview` : null;
}

/**
 * The hostnames a domain-locked embed (Vimeo's "Only on domains I choose") must
 * allowlist for THIS deployment. Three real embedding hosts, not one: the admin
 * origin renders the inline builder preview in its own document, and the member
 * app is served at BOTH the apex and `member.*` (see Caddyfile.prod). Vimeo
 * matches the Referer hostname literally — it does not cover subdomains — so
 * each has to be listed separately. Returns [] outside a browser rather than
 * guessing.
 */
export function embedHostnames(): string[] {
  const out: string[] = [];
  const member = memberOrigin();
  if (member) {
    try {
      const h = new URL(member).hostname;
      out.push(h);
      // Skip the sibling vhost for a dotless dev host ('member.localhost').
      if (h.includes('.') && !h.startsWith('member.')) out.push(`member.${h}`);
    } catch {
      /* not a URL — skip */
    }
  }
  if (typeof window !== 'undefined') out.push(window.location.hostname);
  return [...new Set(out)];
}
